<template>
  <v-container fluid class="pa-0">
    <!-- Estado de Carga -->
    <v-row v-if="cargandoComponente || sincronizando" class="fill-height my-10" align="center" justify="center">
      <v-col class="text-center py-10" cols="12">
        <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
        <div class="mt-4 text-h6 font-weight-bold grey--text text--darken-2">
          {{ sincronizando ? 'Consultando métricas en vivo...' : 'Cargando datos locales...' }}
        </div>
      </v-col>
    </v-row>

    <div v-else-if="cicloActivo">
      <!-- Encabezado -->
      <v-row class="mb-4 d-flex align-center px-4">
        <v-col cols="12" md="6">
          <h2 class="text-h4 font-weight-black primary--text">Detalle de Inversión</h2>
          <p class="text-subtitle-1 grey--text text--darken-1 mb-0">
            Métricas diarias por Ciudad y Sucursal vs Presupuesto.
          </p>
        </v-col>
        <v-col cols="12" md="6" class="text-md-right text-left">
          <v-btn color="primary" @click="fetchData(true)" class="mr-2" :loading="sincronizando" outlined>
            <v-icon left>mdi-cloud-refresh</v-icon>
            Recargar Datos
          </v-btn>
        </v-col>
      </v-row>

      <!-- Resumen y Filtro -->
      <v-row class="px-4 mb-4">
        <v-col cols="12">
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Buscar por sucursal o fecha..."
            filled
            rounded
            dense
            hide-details
            class="elevation-1"
          ></v-text-field>
        </v-col>
      </v-row>

      <!-- Estructura Jerárquica: Ciudad > Sucursal > Tabla Diaria -->
      <div v-if="filteredData.length > 0">
        <v-expansion-panels multiple accordion class="px-2">
          <v-expansion-panel
            v-for="(ciudad, cin) in filteredData"
            :key="`ciudad-${cin}`"
            class="mb-3 border-radius-lg elevation-2 overflow-hidden"
          >
            <v-expansion-panel-header class="bg-light-blue py-4">
              <template v-slot:default="{ open }">
                <v-row no-gutters align="center">
                  <v-col cols="4">
                    <span class="text-h6 font-weight-bold primary--text">{{ ciudad.nombre }}</span>
                  </v-col>
                  <v-col cols="8" class="text-right grey--text text--darken-1 pr-4" v-if="!open">
                    <span class="mr-3">Gasto: <b>{{ ciudad.totalSpend | currency }}</b></span>
                    <span class="mr-3">Contactos: <b>{{ ciudad.totalLeads }}</b></span>
                    <span>Consumo: <b>{{ ciudad.pctConsumido }}</b></span>
                  </v-col>
                </v-row>
              </template>
            </v-expansion-panel-header>
            
            <v-expansion-panel-content class="pa-0 mt-2">
              <v-expansion-panels flat hover>
                <v-expansion-panel
                  v-for="(sucursal, sin) in ciudad.sucursales"
                  :key="`sucursal-${sin}`"
                  class="border-bottom-light"
                >
                  <v-expansion-panel-header class="py-3">
                    <v-row no-gutters align="center">
                      <v-col cols="6">
                        <span class="font-weight-bold grey--text text--darken-3">{{ sucursal.nombre }}</span>
                      </v-col>
                      <v-col cols="6" class="text-right text-caption pr-4">
                        <v-chip x-small color="success" outlined class="font-weight-bold">{{ sucursal.totalLeads }} Leads</v-chip>
                        <span class="ml-2 font-weight-bold">{{ sucursal.totalSpend | currency }}</span>
                      </v-col>
                    </v-row>
                  </v-expansion-panel-header>
                  
                  <v-expansion-panel-content>
                    <v-data-table
                      :headers="headersDetailed"
                      :items="sucursal.dias"
                      dense
                      hide-default-footer
                      disable-pagination
                      class="detailed-table elevation-0"
                    >
                      <template v-slot:item.cpl="{ item }">
                        <v-chip x-small :color="item.cpl > 150 ? 'red lighten-4' : 'green lighten-4'" 
                                :text-color="item.cpl > 150 ? 'red darken-4' : 'green darken-4'"
                                class="font-weight-bold">
                          {{ item.cpl | currency }}
                        </v-chip>
                      </template>
                      <template v-slot:item.budget_estimado="{ item }">
                        <span class="blue--text font-weight-bold">{{ item.budget_estimado | currency }}</span>
                      </template>
                      <template v-slot:item.spend="{ item }">
                        <span :class="parseFloat(item.pct_consumido) > 100 ? 'red--text' : 'green--text font-weight-bold'">
                          {{ item.spend | currency }}
                        </span>
                      </template>
                      <template v-slot:item.pct_consumido="{ item }">
                        <span :class="parseFloat(item.pct_consumido) > 100 ? 'red--text font-weight-bold' : 'grey--text text--darken-2'">
                          {{ item.pct_consumido }}
                        </span>
                      </template>
                    </v-data-table>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>

      <v-row v-else class="my-10 text-center">
        <v-col cols="12">
          <v-icon size="64" color="grey lighten-2">mdi-database-off</v-icon>
          <p class="grey--text mt-2">No se encontraron datos para los filtros aplicados.</p>
        </v-col>
      </v-row>
    </div>
  </v-container>
</template>

<script>
import moment from 'moment';

export default {
  name: 'TabDetalleMetaGoogle',
  props: {
    instancia: { type: String, required: true },
    isDirector: { type: Boolean, default: false },
    cicloId: { type: [Number, String], default: null },
    isActivo: { type: Boolean, default: false }
  },
  
  data() {
    return {
      cargandoComponente: false,
      sincronizando: false,
      cicloActivo: null,
      search: '',
      
      headersDetailed: [
        { text: 'Fecha', value: 'fecha', align: 'start' },
        { text: 'Red', value: 'canal' },
        { text: 'Presp. D.', value: 'budget_estimado' },
        { text: 'Gasto', value: 'spend' },
        { text: '% Cons.', value: 'pct_consumido' },
        { text: 'Leads', value: 'leads' },
        { text: 'CPL', value: 'cpl' },
      ],
      
      itemsMapeados: []
    }
  },
  computed: {
    activeCycleInfo() {
      return this.cicloActivo || {};
    },
    filteredData() {
      if(!this.itemsMapeados || this.itemsMapeados.length === 0) return [];
      
      // Agrupar por Ciudad y Sucursal
      const res = {};
      const s = this.search.toLowerCase();

      this.itemsMapeados.forEach(item => {
          // Filtro de búsqueda básico
          if (s && !item.ciudad.toLowerCase().includes(s) && !item.sucursal.toLowerCase().includes(s) && !item.fecha.toLowerCase().includes(s)) {
              return;
          }

          if(!res[item.ciudad]) {
              res[item.ciudad] = { nombre: item.ciudad, sucursales: {}, totalSpend: 0, totalLeads: 0, totalBudget: 0 };
          }
          if(!res[item.ciudad].sucursales[item.sucursal]) {
              res[item.ciudad].sucursales[item.sucursal] = { nombre: item.sucursal, dias: [], totalSpend: 0, totalLeads: 0, totalBudget: 0 };
          }
          
          res[item.ciudad].sucursales[item.sucursal].dias.push(item);
          
          // Acumuladores Sucursal
          res[item.ciudad].sucursales[item.sucursal].totalSpend += item.spend;
          res[item.ciudad].sucursales[item.sucursal].totalLeads += item.leads;
          res[item.ciudad].sucursales[item.sucursal].totalBudget += (parseFloat(item.budget_estimado) || 0);

          // Acumuladores Ciudad
          res[item.ciudad].totalSpend += item.spend;
          res[item.ciudad].totalLeads += item.leads;
          res[item.ciudad].totalBudget += (parseFloat(item.budget_estimado) || 0);
      });

      // Convertir a array para el template
      return Object.values(res).map(c => {
          const pct = c.totalBudget > 0 ? ((c.totalSpend / c.totalBudget) * 100).toFixed(1) + '%' : '0%';
          return {
              ...c,
              pctConsumido: pct,
              sucursales: Object.values(c.sucursales).sort((a,b) => a.nombre.localeCompare(b.nombre))
          };
      }).sort((a,b) => a.nombre.localeCompare(b.nombre));
    }
  },
  watch: {
    cicloId: {
      immediate: true,
      handler(newId) {
        if (newId) {
          this.fetchData(false);
        }
      }
    }
  },
  
  methods: {
    async fetchData(forceRefresh = false) {
      if (!this.cicloId) return;
      this.cargandoComponente = true;
      if (forceRefresh) this.sincronizando = true;
      
      try {
        // 1. Cargar el ciclo
        const resCiclo = await this.$http.get(`api/presupuestos/ciclo/${this.cicloId}`);
        if(!resCiclo.data) throw new Error("Ciclo no encontrado");
        this.cicloActivo = resCiclo.data;

        // Limites de Búsqueda
        // Limites de Búsqueda
        const dateStart = (this.cicloActivo.fecha_inicio || "").substring(0, 10);
        let dateStop = new Date().toISOString().substring(0, 10);
        const finCiclo = (this.cicloActivo.fecha_fin || "").substring(0, 10);
        if (dateStop > finCiclo) dateStop = finCiclo;
        // 2. Extraer Rendimiento Diario para sacar "Presupuesto Asignado por Día"
        const resRendDiario = await this.$http.get(`api/presupuestos/rendimiento-diario/${this.cicloId}?instancia=${this.instancia}`);
        const diasDistribucion = resRendDiario.data && resRendDiario.data.dias ? resRendDiario.data.dias : [];

        // 2.1 Obtener Semanas y sus Distribuciones de Canal para calcular el Presupuesto por Canal
        const resSemanas = await this.$http.get(`api/presupuestos/semanas/${this.cicloId}`);
        const semanas = resSemanas.data || [];
        
        // Obtener distribuciones de canal para cada semana
        const distPromesas = semanas.map(s => this.$http.get(`api/presupuestos/distribucion-canal/${s.id}`));
        const resDistCanales = await Promise.all(distPromesas);
        const distCanalesMap = {}; // semanaId -> { 'Meta Ads': %, 'Google Ads': % }
        
        resDistCanales.forEach((res, index) => {
            const semId = semanas[index].id;
            distCanalesMap[semId] = {};
            if (res.data && Array.isArray(res.data)) {
                res.data.forEach(d => {
                    distCanalesMap[semId][d.nombre] = parseFloat(d.porcentaje_del_canal) || 0;
                });
            }
        });

        // 3. Obtener Data Real de Meta 
        let dataMeta = [];
        try {
            if(!forceRefresh) {
               const cache = await this.$http.get(`api/presupuestos/api-cache/${this.cicloId}/Meta`);
               if (cache.data && cache.data.data) dataMeta = cache.data.data;
            }
            if(dataMeta.length === 0 || forceRefresh) {
               const res = await this.$http.post('api/presupuestos/meta/spend', { instancia: this.instancia, date_start: dateStart, date_stop: dateStop });
               if(res.data) dataMeta = res.data;
               this.$http.post('api/presupuestos/api-cache', { id_presupuesto_ciclo: this.cicloId, canal: 'Meta', response_json: res.data }).catch(e=>e);
            }
        } catch(e) { console.warn("Meta Err", e); }

        // 4. Obtener Data Real de Google
        let dataGoogle = [];
        try {
            if(!forceRefresh) {
               const cache = await this.$http.get(`api/presupuestos/api-cache/${this.cicloId}/Google`);
               if (cache.data && cache.data.data) dataGoogle = cache.data.data;
            }
            if(dataGoogle.length === 0 || forceRefresh) {
               const res = await this.$http.post('api/presupuestos/google/spend', { instancia: this.instancia, date_start: dateStart, date_stop: dateStop });
               if(res.data) dataGoogle = res.data;
               this.$http.post('api/presupuestos/api-cache', { id_presupuesto_ciclo: this.cicloId, canal: 'Google', response_json: res.data }).catch(e=>e);
            }
        } catch(e) { console.warn("Google Err", e); }

        // Extraer y agrupar todo el Gasto y Leads real
        let spendData = [];
        dataMeta.forEach(m => {
             const loc = this.detectarSucursal(m.campaign_name, 'Meta');
             spendData.push({ fecha: m.date, plataforma: 'Meta', ciudad: loc.ciudad, sucursal: loc.sucursal, spend: parseFloat(m.spend)||0, leads: parseInt(m.whatsapp_conversations)||0 });
        });
        
        dataGoogle.forEach(g => {
             const loc = this.detectarSucursal(g.campaign_name, 'Google');
             spendData.push({ fecha: g.date, plataforma: 'Google', ciudad: loc.ciudad, sucursal: loc.sucursal, spend: parseFloat(g.spend)||0, leads: parseInt(g.conversions)||0 });
        });

        // Agrupar los datos reales por Fecha + Plataforma + Ciudad + Sucursal
        let mapaFinal = {};
        spendData.forEach(item => {
             let k = `${item.fecha}|${item.plataforma}|${item.ciudad}|${item.sucursal}`;
             if (!mapaFinal[k]) {
                 mapaFinal[k] = { ...item, spend: 0, leads: 0, budget_estimado: 0 };
             }
             mapaFinal[k].spend += item.spend;
             mapaFinal[k].leads += item.leads;
        });

        // Asignar presupuestos (distribución diaria teórica) hacia los diccionarios reales
        let diaIterador = moment(dateStart);
        const hoyLimit = moment(dateStop);
        while(diaIterador.isSameOrBefore(hoyLimit, 'day')) {
             let fStr = diaIterador.format('YYYY-MM-DD');
             let propDia = diasDistribucion.find(d => d.fecha === fStr);
             
             if (propDia) {
                const semActual = semanas.find(s => {
                    const ini = (s.fecha_inicio_semana || s.fecha_inicio || "").substring(0, 10);
                    const fin = (s.fecha_fin_semana || s.fecha_fin || "").substring(0, 10);
                    return fStr >= ini && fStr <= fin;
                });
                const distCanal = semActual ? distCanalesMap[semActual.id] : null;
                
                const pctMeta = Number((distCanal && distCanal['Meta Ads'] !== undefined) ? distCanal['Meta Ads'] : 70);
                const pctGoogle = Number((distCanal && distCanal['Google Ads'] !== undefined) ? distCanal['Google Ads'] : 30);
                const previsto = Number(propDia.previsto) || 0;

                const presDiaMeta = (previsto * pctMeta / 100);
                const presDiaGoogle = (previsto * pctGoogle / 100);
                
                const macroCiudades = ['MTY', 'CDMX', 'GDL', 'Online'];
                macroCiudades.forEach(ciudad => {
                    let budgetMetaCiudad = presDiaMeta / macroCiudades.length;
                    let budgetGoogleCiudad = presDiaGoogle / macroCiudades.length;
                    
                    let sucursalesMetaActivas = Object.values(mapaFinal).filter(x => x.fecha === fStr && x.plataforma === 'Meta' && x.ciudad === ciudad);
                    let sucursalesGoogleActivas = Object.values(mapaFinal).filter(x => x.fecha === fStr && x.plataforma === 'Google' && x.ciudad === ciudad);
                    
                    if (sucursalesMetaActivas.length > 0) {
                        let presPorSucursal = budgetMetaCiudad / sucursalesMetaActivas.length;
                        sucursalesMetaActivas.forEach(suc => suc.budget_estimado = presPorSucursal);
                    } else {
                        let k = `${fStr}|Meta|${ciudad}|Sin Gasto`;
                        mapaFinal[k] = { fecha: fStr, plataforma: 'Meta', ciudad, sucursal: 'General', spend: 0, leads: 0, budget_estimado: budgetMetaCiudad };
                    }

                    if (sucursalesGoogleActivas.length > 0) {
                        let presPorSucursal = budgetGoogleCiudad / sucursalesGoogleActivas.length;
                        sucursalesGoogleActivas.forEach(suc => suc.budget_estimado = presPorSucursal);
                    } else {
                        let k = `${fStr}|Google|${ciudad}|Sin Gasto`;
                        mapaFinal[k] = { fecha: fStr, plataforma: 'Google', ciudad, sucursal: 'General', spend: 0, leads: 0, budget_estimado: budgetGoogleCiudad };
                    }
                });
             }

             diaIterador.add(1, 'days');
        }

        this.itemsMapeados = Object.values(mapaFinal).map(item => {
             let cpl = item.leads > 0 ? (item.spend / item.leads) : 0;
             let pctNum = item.budget_estimado > 0 ? (item.spend / item.budget_estimado) * 100 : 0;
             let pct = pctNum.toFixed(1) + '%';
             return {
                 ciudad: item.ciudad,
                 sucursal: item.sucursal,
                 fecha: moment(item.fecha).format('DD MMM YYYY'),
                 fecha_raw: item.fecha,
                 canal: item.plataforma,
                 budget_estimado: item.budget_estimado,
                 spend: item.spend,
                 leads: item.leads,
                 cpl: cpl,
                 pct_consumido: pct
             };
        }).sort((a,b) => b.fecha_raw.localeCompare(a.fecha_raw)); // Descending by Date
        
      } catch (err) {
        console.error("Error trayendo Detalle:", err);
      } finally {
        this.cargandoComponente = false;
        this.sincronizando = false;
      }
    },

    detectarSucursal(campaignName, plataforma) {
        if (!campaignName) return { ciudad: 'General', sucursal: 'General' };
        const upper = campaignName.toUpperCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
               
        if (upper.includes("ONLINE")) return { ciudad: 'Online', sucursal: 'Online' };
               
        // FAST
        if (upper.includes("NORIA")) return { ciudad: 'MTY', sucursal: 'La Noria' };
        if (upper.includes("ROMULO") || upper.includes("GARZA")) return { ciudad: 'MTY', sucursal: 'Rómulo Garza' };
        if (upper.includes("UNIVERSIDAD") || upper.includes("UNI ")) return { ciudad: 'MTY', sucursal: 'Universidad' };
        if (upper.includes("MITRAS")) return { ciudad: 'MTY', sucursal: 'Mitras' };
        if (upper.includes("ELOY") || upper.includes("CAVAZOS")) return { ciudad: 'MTY', sucursal: 'Eloy Cavazos' };
        if (upper.includes("LINDA") || upper.includes("VISTA") || upper.includes("LINDAVISTA")) return { ciudad: 'MTY', sucursal: 'Linda Vista' };
        if (upper.includes("GUADALUPE")) return { ciudad: 'MTY', sucursal: 'Guadalupe' };
               
        // INBI
        if (upper.includes("LINCOLN")) return { ciudad: 'MTY', sucursal: 'Lincoln' };
        if (upper.includes("SANTA CATARINA") || upper.includes("MTY SANTA")) return { ciudad: 'MTY', sucursal: 'Santa Catarina' };
        if (upper.includes("ESCOBEDO")) return { ciudad: 'MTY', sucursal: 'Escobedo' };
        if (upper.includes("FRESNOS")) return { ciudad: 'MTY', sucursal: 'Fresnos' };
        if (upper.includes("MIGUEL ALEMAN") || upper.includes("MIGUEL ALEMAN")) return { ciudad: 'MTY', sucursal: 'Miguel Alemán' };
        if (upper.includes("PABLO LIVAS")) return { ciudad: 'MTY', sucursal: 'Pablo Livas' };
        if (upper.includes("CASA BLANCA")) return { ciudad: 'MTY', sucursal: 'Casa Blanca' };
        if (upper.includes("SAN MIGUEL")) return { ciudad: 'MTY', sucursal: 'San Miguel' };
        if (upper.includes("APODACA")) return { ciudad: 'MTY', sucursal: 'Apodaca' };
               
        if (upper.includes("CUITLAHUAC")) return { ciudad: 'CDMX', sucursal: 'Cuitláhuac' };
        if (upper.includes("MISTERIOS")) return { ciudad: 'CDMX', sucursal: 'Misterios' };
        if (upper.includes("MONTEVIDEO")) return { ciudad: 'CDMX', sucursal: 'Montevideo' };
        if (upper.includes("ANAHUAC") || upper.includes("ANAHUAC")) return { ciudad: 'CDMX', sucursal: 'Anáhuac' };
        if (upper.includes("ARAGON") || upper.includes("ARAGON")) return { ciudad: 'CDMX', sucursal: 'Aragón' };
               
        if (upper.includes("REVOLUCION") || (upper.includes("GDL") && !upper.includes("TRANSITO"))) return { ciudad: 'GDL', sucursal: 'Guadalajara Revolución' };
        if (upper.includes("TRANSITO")) return { ciudad: 'GDL', sucursal: 'Guadalajara Tránsito' };
        
        // Fallbacks según plataforma/nomenclatura antigua si no coincidió el plantel
        if (plataforma === 'Meta') {
            if (upper.includes('CDMX')) return { ciudad: 'CDMX', sucursal: 'General' };
            if (upper.includes('GDL') || upper.includes('GUADALAJARA')) return { ciudad: 'GDL', sucursal: 'General' };
            if (upper.includes('MTY') || upper.includes('SAN NICOLAS')) return { ciudad: 'MTY', sucursal: 'General' };
            if (upper.includes('TOLUCA')) return { ciudad: 'Toluca', sucursal: 'Toluca' };
        } else {
            if (upper.includes('CDMX') || upper.includes('METROBUS')) return { ciudad: 'CDMX', sucursal: 'General' };
            if (upper.includes('GDL') || upper.includes('GUADALAJARA') || upper.includes('TREN LIGERO') || upper.includes('ZAPOPAN')) return { ciudad: 'GDL', sucursal: 'General' };
            if (upper.includes('MTY') || upper.includes('SAN NICOLAS')) return { ciudad: 'MTY', sucursal: 'General' };
            if (upper.includes('TOLUCA')) return { ciudad: 'Toluca', sucursal: 'Toluca' };
        }

        return { ciudad: 'General', sucursal: 'General' };
    }
  },

  filters: {
    currency(value) {
      if (value === null || value === undefined) return "$0.00";
      const num = parseFloat(value);
      if (isNaN(num)) return value;
      return "$" + num.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
    }
  }
}
</script>

<style scoped>
.bg-light-blue {
  background-color: #f0f7ff !important;
}
.border-radius-lg {
  border-radius: 12px !important;
}
.border-bottom-light {
  border-bottom: 1px solid #f0f0f0;
}
.detailed-table >>> th {
  background-color: #fafafa !important;
  font-size: 0.75rem !important;
  color: #777 !important;
  text-transform: uppercase;
}
.detailed-table >>> td {
  font-size: 0.85rem !important;
}
</style>
